<script lang="ts" setup>
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { navigation } from '@/composition/navigation'
import { SearchState } from '@/composition/search'
import { MemberStatus } from '@/models/Groups'
import NavigationItem from '@/models/Navigation'
import { useGlobalStore } from '@/store/app'
import { getNumberAbbreviation } from '@/util/NumberUtils'
import { isGokollab } from '@ghl/clientportal-core/init'
import { UITabPane, UITabs, UITextMdMedium } from '@gohighlevel/ghl-ui'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import ShimmerLoader from '../../animations/ShimmerLoader.vue'
import CommunityButton from '../../buttons/CommunityButton.vue'

const LoginModal = defineAsyncComponent(
  () => import('@/components/login/LoginModal.vue')
)
const InvitationLinkModal = defineAsyncComponent(
  () => import('@/components/groups/InvitationLinkModal.vue')
)
const GroupInvitations = defineAsyncComponent(
  () => import('@/components/groups/settings/GroupInvitations.vue')
)

const { t } = useI18n()
const appStore = useGlobalStore()
const router = useRouter()
const route = useRoute()
const tabSwitcher = ref<HTMLElement | null>(null)
const loginPage = ref('LOGIN')
const showLoginModal = ref(false)
const showInvitationModal = ref(false)
const mobileTabsRef = ref<HTMLElement | null>(null)

const handleToggle = (item: NavigationItem) => {
  if (GroupState.state.closedGroup) {
    return
  }
  if (
    ['group-home-search', 'channel-search'].includes(
      router.currentRoute.value.name as string
    )
  ) {
    return
  }
  router.push({ name: item.routeName[0] })
}

function getNavigationLink(item: NavigationItem) {
  if (GroupState.state.closedGroup) {
    return
  }
  if (
    ['group-home-search', 'channel-search'].includes(
      router.currentRoute.value.name as string
    )
  ) {
    return
  }

  let routePath = item.routeName[0]
  if (item.routeName[0] === 'group-member-list') {
    routePath = 'member-list'
  }
  if (item.routeName[0] === 'learning-home') {
    routePath = 'learning'
  }
  if (isGokollab()) {
    return `/${route.params.groupSlug}/${routePath}`
  }

  return `/communities/groups/${route.params.groupSlug}/${routePath}`
}

function openLogInModal() {
  showLoginModal.value = true
  loginPage.value = 'LOGIN'
}

const isSearchRoute = computed(() => {
  return [
    'group-home-search',
    'channel-search',
    'channel-search-post-view',
    'group-search-post-view',
  ].includes(router.currentRoute.value.name as string)
})

const filteredNavigationItems = computed(() => {
  return navigation.state.navigationItems?.filter(i => {
    if (isSearchRoute.value) {
      return i.showOnWeb && i.isSearchAvailable
    }
    return i.showOnWeb
  })
})

const currentTab = computed(() => {
  const currentRoute = route.name as string
  return (
    filteredNavigationItems.value?.find(item =>
      item.routeName.includes(currentRoute)
    )?.name || filteredNavigationItems.value?.[0]?.name
  )
})

const handleTabChange = (tabName: string) => {
  const selectedItem = filteredNavigationItems.value?.find(
    item => item.name === tabName
  )
  if (selectedItem) {
    handleToggle(selectedItem as NavigationItem)
  }
}

// Function to scroll the selected tab into view
const scrollToSelectedTab = () => {
  nextTick(() => {
    let activeTab: HTMLElement | null = null

    if (app.state.isMobile) {
      // Search within the mobile tabs container
      activeTab = mobileTabsRef.value?.querySelector(
        `[data-name="${currentTab.value}"]`
      ) as HTMLElement
    } else {
      activeTab = tabSwitcher.value?.querySelector(
        `[data-name="${currentTab.value}"]`
      ) as HTMLElement
    }
    if (activeTab) {
      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  })
}

// Scroll to selected tab when component mounts
onMounted(() => {
  // Add a small delay to ensure the DOM is fully rendered
  setTimeout(() => {
    scrollToSelectedTab()
  }, 100)
})

watch(currentTab, () => {
  scrollToSelectedTab()
})
</script>
<template>
  <!-- Mobile View -->
  <div v-if="app.state.isMobile" ref="mobileTabsRef" class="w-full px-4">
    <UITabs
      type="line"
      :animated="true"
      placement="top"
      :value="currentTab"
      class="communities-mobile-tabs"
      @update:value="handleTabChange"
    >
      <UITabPane
        v-for="item in filteredNavigationItems"
        :key="item.name"
        :name="item.name"
        :tab="t(item.label)"
      >
        <span
          v-if="
            isSearchRoute &&
            (SearchState.state.searchDiscussionResults?.[0]?.total > 0 ||
              !SearchState.state.fetching)
          "
          class="ml-2"
        >
          {{
            getNumberAbbreviation(
              SearchState.state.searchDiscussionResults?.[0]?.total ?? 0
            )
          }}
        </span>
      </UITabPane>
    </UITabs>
  </div>

  <!-- Web View -->
  <div
    v-else
    ref="tabSwitcher"
    class="flex w-full max-w-[1158px] items-center justify-between whitespace-nowrap"
    :class="[
      !app.state.isMobile && 'mx-6',
      app.state.readingMode &&
        GroupState.state.userStatus === MemberStatus.ACTIVE &&
        !app.state.isMobile &&
        'ml-16 xl:ml-6',
    ]"
  >
    <div
      class="flex cursor-pointer overflow-x-auto text-base text-communities-font-secondary"
    >
      <div
        v-for="item in filteredNavigationItems"
        :id="`communities-${item.name.toLowerCase().replace('/', '-')}-btn`"
        :key="item.name"
        class="border-[3px] border-l-0 border-r-0 border-t-0 border-solid border-communities-background px-4 py-4 text-base font-medium hover:text-communities-font-primary"
        :active="item && item.routeName.includes(route.name as never)"
        :class="{
          'border-communities-font-primary  text-communities-font-primary': item && item.routeName.includes(route.name as never),
        }"
        tabindex="0"
        role="button"
        aria-label="Tab Switcher"
        @click="() => handleToggle(item as NavigationItem)"
        @keyup.enter="() => handleToggle(item as NavigationItem)"
      >
        <a
          :href="getNavigationLink(item as NavigationItem)"
          class="text-inherit no-underline"
        >
          <div @click.stop.prevent="handleToggle(item as NavigationItem)">
            <span> {{ t(item.label) }} </span>
          </div>
        </a>
        <span
          v-if="
            isSearchRoute &&
            (SearchState.state.searchDiscussionResults?.[0]?.total > 0 ||
              !SearchState.state.fetching)
          "
          class="ml-2"
        >
          {{
            getNumberAbbreviation(
              SearchState.state.searchDiscussionResults?.[0]?.total ?? 0
            )
          }}</span
        >
      </div>
    </div>
    <div>
      <div
        v-if="!appStore.isAuthenticated && !app.state.isMobile"
        class="flex gap-2"
      >
        <!-- Shimmer loader for login button -->
        <ShimmerLoader
          v-if="app.state.fetching"
          :repeat="1"
          class-name="h-10 w-28 rounded-md bg-communities-surface"
        />
        <CommunityButton
          v-else
          id="login_button "
          class="font flex !h-10 w-28 gap-2"
          button-type="tertiary"
          @click="openLogInModal"
        >
          <UITextMdMedium>LOG IN</UITextMdMedium>
        </CommunityButton>
      </div>
    </div>
  </div>
  <LoginModal
    v-if="showLoginModal"
    :page="loginPage"
    :location-data="app.state.locationData"
    :show="showLoginModal"
    :login-confirmation="() => {}"
    @update:show="showLoginModal = false"
  />
  <InvitationLinkModal
    v-if="showInvitationModal"
    :show="showInvitationModal"
    @update:show="showInvitationModal = false"
  />
  <GroupInvitations v-if="app.state.showInviteModal" />
</template>
<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.overflow-x-auto {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.communities-mobile-tabs :deep(.ui-tabs-nav) {
  margin-bottom: 0;
  padding: 0 16px;
}

.communities-mobile-tabs :deep(.ui-tabs-ink-bar) {
  background-color: var(--communities-font-primary);
}

.communities-mobile-tabs :deep(.ui-tabs-tab) {
  color: var(--communities-font-secondary);
  font-size: 16px;
  padding: 12px 0;
}

.communities-mobile-tabs :deep(.ui-tabs-tab-active) {
  color: var(--communities-font-primary);
}

.n-tabs,
.n-tabs-tab {
  --n-tab-font-size: 16px !important;
}

.n-tab-pane {
  padding: 0 !important;
}
</style>
